import { useMemo, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar, TopBar, type NavItem, type NavChild } from '@kss/ui';
import { SessionEndedBanner } from '@kss/auth';
import { useDynamicMenu, type SidebarNode } from './useDynamicMenu';

export interface PanelLayoutProps {
  /** Optional slot rendered in the TopBar (next to the language switcher).
   *  Lets the host app (apps/panel, apps/web) inject panel-specific
   *  affordances — offline badge, branch switcher, etc. — without
   *  taking a hard dep on those modules from @kss/layouts. */
  topbarExtras?: ReactNode;
}

/**
 * Backoffice shell. The sidebar tree is sourced live from the API:
 *
 *   GET /api/Menu/all      → every menu node for the tenant
 *   GET /api/RoleMenu/all  → which roles can see which menus
 *
 * Falls back to the static `sidebarNav.json` baked into `@kss/ui`
 * whenever the API returns an empty tree (offline mode, un-seeded
 * tenant, the request errored). That keeps a working sidebar in the
 * UI showcase package and on first-launch tenants.
 */
export function PanelLayout({ topbarExtras }: PanelLayoutProps = {}) {
  const { nodes } = useDynamicMenu();

  // SidebarNode (live API shape) → NavItem (UI package shape).
  //
  // DEFAULT: hand `undefined` to <Sidebar> so it renders the static
  // `sidebarNav.json` baked into @kss/ui — the COMPLETE, known-good menu
  // (every module: Sales, Products, Restaurant → Menu Categories / Menu
  // Items / QR Codes / Tables, HRM, ESS, …) wired to real `/panel/...`
  // routes and visible to every panel user (admin sees all).
  //
  // Why static-by-default: the live `/api/Menu` tree (112 rows) is seeded
  // for grouping but its restaurant section omits Menu Categories / Menu
  // Items and carries stale `url` paths — so building the sidebar from it
  // gives admins a partial, half-broken menu (the reported "restaurant
  // menus not showing"). The static config is the authoritative superset.
  //
  // Opt back into the API-driven tree with VITE_USE_API_MENU=Y once the
  // backend seeds a complete menu tree with correct paths + admin grants
  // (see G62 / §48).
  const navItems = useMemo<NavItem[] | undefined>(() => {
    const forceApi = /^Y(es)?$|^true$|^1$/i.test(
      ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_USE_API_MENU ?? '').trim(),
    );
    if (!forceApi) return undefined; // → static (complete) sidebar
    if (!nodes || nodes.length === 0) return undefined;
    return nodes.map(toNavItem);
  }, [nodes]);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <Sidebar items={navItems} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <SessionEndedBanner />
        <TopBar extras={topbarExtras} />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function toNavItem(node: SidebarNode): NavItem {
  return {
    id: node.id,
    label: node.name,
    labelKey: `nav.${node.name}`,
    icon: node.icon ?? 'Square',
    path: node.path,
    children: node.children?.map(toNavChild),
  };
}

function toNavChild(node: SidebarNode): NavChild {
  return {
    id: node.id,
    label: node.name,
    labelKey: `nav.${node.name}`,
    path: node.path ?? '#',
  };
}

export default PanelLayout;
